import { Injectable } from '@angular/core';
import {Facility} from "../../../../server/entities/facility.entity";
import {FacilityGetRequest} from "../../../../server/services/facility/get.contract";
import {News} from "../../../../server/entities/news.entity";
import {NewsGetRequest} from "../../../../server/services/news/get.contract";
import {NewsQueryRequest} from "../../../../server/services/news/query.contract";
import {Gathering} from "../../../../server/entities/gathering.entity";
import {GatheringQueryRequest} from "../../../../server/services/gathering/query.contract";
import {Picture} from "../../../../server/entities/facility/picture.enitity";
import {FacilityPictureQueryRequest} from "../../../../server/services/facility/picture/query.contract";
import {VideoFile} from "../../../../server/entities/video-file.entity";
import {VideoQueryRequest} from "../../../../server/services/video/query.contract";
import {
  NewsQueryPaginatedRequest,
  NewsQueryPaginatedResponse
} from "../../../../server/services/news/queryPaginated.contract";
import {LifeHack} from "../../../../server/entities/lifeHack.entity";
import {LifeHackQueryRequest} from "../../../../server/services/lifeHack/query.contract";
import {
  GatheringQueryPaginatedRequest,
  GatheringQueryPaginatedResponse
} from "../../../../server/services/gathering/queryPaginated.contract";
import dayjs from "dayjs";
import {GatheringGetRequest} from "../../../../server/services/gathering/get.contract";
import {User} from "../../../../server/entities/user.entity";
import {UserGetRequest} from "../../../../server/services/user/get.contract";
import {UserUpdateRequest} from "../../../../server/services/user/update.contract";
import {GeoLocationType} from "../../../../server/entities/types/geoLocation.type";
import { Geolocation } from "@awesome-cordova-plugins/geolocation/ngx";
import {
  FacilityQueryPaginatedRequest,
  FacilityQueryPaginatedResponse
} from "../../../../server/services/facility/queryPaginated.contract";
import {WebAuthenticationService} from "../security/web-authentication.service";
import {NextOffer, NextOfferType} from "../../../../server/entities/nextOffer.entity";
import {
  NextQueryPaginatedRequest,
  NextQueryPaginatedResponse
} from "../../../../server/services/next/queryPaginated.contract";
import {
  NextGetModuleStatusRequest,
  NextGetModuleStatusResponse
} from "../../../../server/services/next/getModuleStatus.contract";
import {BehaviorSubject} from "rxjs";
import {LocationSnapshot, LocationSource} from "./location-snapshot";
import {
  GeolocationGetAddressFromCoordinatesRequest,
  GeolocationGetAddressFromCoordinatesResponse,
} from "../../../../server/services/geolocation/getAddressFromCoordinates.contract";

export {LocationSnapshot, LocationSource} from "./location-snapshot";

@Injectable({
  providedIn: 'root'
})
export class SharedDataService {
  private facilities: Map<string, Facility> = new Map<string, Facility>();

  public news: Map<string, News> = new Map<string, News>();
  newsPage: number = 1;
  newsPageSize: number = 4;
  newsTotalCount: number = 999999;

  private lifeHacks: Map<string, LifeHack> = new Map<string, LifeHack>();
  private newsByFacility: Map<string, News[]> = new Map<string, News[]>();
  private gatheringsByFacility: Map<string, Gathering[]> = new Map<string, Gathering[]>();
  private picturesByFacility: Map<string, Picture[]> = new Map<string, Picture[]>();
  private videosByFacility: Map<string, VideoFile[]> = new Map<string, VideoFile[]>();

  public gatherings: Map<string, Gathering> = new Map<string, Gathering>();
  gatheringPage: number = 1;
  gatheringPageSize: number = 3;
  gatheringTotalCount: number = 999999;

  public facilityClubs: Map<string, Facility> = new Map<string, Facility>();
  facilityClubsPage: number = 1;
  facilityClubsPageSize: number = 3;
  facilityClubsTotalCount: number = 999999;

  public facilityConsulting: Map<string, Facility> = new Map<string, Facility>();
  facilityConsultingPage: number = 1;
  facilityConsultingPageSize: number = 3;
  facilityConsultingTotalCount: number = 999999;

  public facilityOther: Map<string, Facility> = new Map<string, Facility>();
  facilityOtherPage: number = 1;
  facilityOtherPageSize: number = 3;
  facilityOtherTotalCount: number = 999999;

  // "next" module (NEXT_MODULE_PLAN.md §6.1): one distance-sorted slider per
  // offer category. Same map/page/totalCount pattern as the facility sliders,
  // just keyed by NextOfferType instead of three copies of everything.
  public nextOffers: { [offerType: string]: Map<string, NextOffer> } = {
    [NextOfferType.ausbildung]: new Map<string, NextOffer>(),
    [NextOfferType.studium]: new Map<string, NextOffer>(),
    [NextOfferType.fsjbfd]: new Map<string, NextOffer>(),
  };
  nextOffersPage: { [offerType: string]: number } = {
    [NextOfferType.ausbildung]: 1,
    [NextOfferType.studium]: 1,
    [NextOfferType.fsjbfd]: 1,
  };
  nextOffersPageSize: number = 3;
  nextOffersTotalCount: { [offerType: string]: number } = {
    [NextOfferType.ausbildung]: 999999,
    [NextOfferType.studium]: 999999,
    [NextOfferType.fsjbfd]: 999999,
  };
  private nextModuleStatus: NextGetModuleStatusResponse | null = null;

  private user: User;
  private location: GeoLocationType;

  /**
   * Reported `accuracy` (in meters) of `this.location`. Lower is better;
   * `null` means we have no baseline yet and any fix is acceptable.
   *
   * The geolocation plugin/OS will sometimes return very coarse fallback
   * fixes (e.g. a cell-tower fix with 1600m accuracy) when GPS isn't
   * available. We compare incoming fixes to this baseline so we don't
   * overwrite a precise WiFi/GPS position with a coarse cell-tower one.
   */
  private locationAccuracy: number | null = null;

  /** Provenance of `this.location` — see {@link LocationSource}. */
  private locationSource: LocationSource | null = null;
  /** `Date.now()` at the moment `this.location` was last set. */
  private locationTimestamp: number | null = null;

  /** How much accuracy degradation we tolerate before discarding a fix. */
  private static readonly ACCURACY_DEGRADATION_FACTOR = 1.5;

  /**
   * In-flight promises for `updateUserLocation()` so concurrent callers
   * (e.g. all three facility queries kicking off in parallel) share one
   * geolocation request instead of triggering several GPS fixes. The two
   * accuracy modes are tracked separately so a fast/low-accuracy call
   * cannot accidentally satisfy a pending high-accuracy request.
   */
  private fastLocationFetchPromise: Promise<void> | null = null;
  private highAccuracyLocationFetchPromise: Promise<void> | null = null;

  /**
   * Emits every time `this.location` is updated (initial cache hydration,
   * fast fix, or high-accuracy refinement). Consumers like the nearby-
   * places map subscribe to keep the user marker and view in sync without
   * having to run their own GPS request.
   *
   * Emits raw coordinates only — for the full snapshot (source, accuracy,
   * timestamp) subscribe to {@link locationSnapshot$} instead.
   */
  public readonly location$ = new BehaviorSubject<GeoLocationType | null>(null);

  /**
   * Same emission cadence as {@link location$} but with the metadata
   * the UI needs to communicate the situation to the user (where the
   * value came from, how stale / accurate it is). Pages that render
   * the "Ergebnisse für deinen Standort" hint subscribe here.
   */
  public readonly locationSnapshot$ = new BehaviorSubject<LocationSnapshot | null>(null);

  /**
   * Emits the set of facility IDs the signed-in user has subscribed to
   * ("liked"). Updated whenever the cached user is (re)fetched
   * (`getUser`), mutated via `updateUser`, or cleared via `unsetUser`.
   *
   * Consumers (e.g. the nearby-places map) subscribe to this so their
   * UI stays in sync when the user (un)subscribes from somewhere else
   * in the app — without each consumer having to re-fetch the user
   * record on every focus.
   *
   * Always emits a fresh Set instance so RxJS distinct/equality checks
   * and Set identity comparisons work as expected.
   */
  public readonly subscribedFacilities$ = new BehaviorSubject<Set<string>>(new Set());

  private static readonly LOCATION_CACHE_KEY = 'yoggl.lastKnownLocation';

  /**
   * Last coordinate we successfully reverse-geocoded, kept so we can
   * skip the network round-trip while the user is jittering inside the
   * same neighbourhood. Reset only when we move noticeably (see
   * {@link REVERSE_GEOCODE_DISTANCE_M}).
   */
  private lastReverseGeocodeLat: number | null = null;
  private lastReverseGeocodeLng: number | null = null;
  private reverseGeocodeInFlight: boolean = false;
  private static readonly REVERSE_GEOCODE_DISTANCE_M = 50;

  /**
   * Names of map filter categories the user has disabled on the nearby-places
   * map. Persisted in-memory for the duration of the app session so the
   * selection survives navigating away from and back to the map page.
   */
  private mapFilterDisabledCategories: Set<string> = new Set<string>();

  constructor(
    private geolocation: Geolocation,
    private webAuthenticationService: WebAuthenticationService
  ) {
    const cached = this.readCachedLocation();
    if (cached) {
      this.applyLocation(cached.location, cached.accuracy, 'gps-cache', cached.savedAt, { persist: false });
    }
  }

  /**
   * Seed the service with an authoritative location (e.g. the value
   * stored on the signed-in user's account record, or a previously
   * persisted device fix). Used as the accuracy baseline so subsequent
   * coarse-fallback fixes from the OS (cell-tower, etc.) get rejected
   * before they can clobber the pin.
   *
   * Callers pass a *conservative* accuracy estimate — typically 50m for
   * a stored "good" position. Anything notably coarser than that will
   * be discarded by `updateUserLocation`.
   *
   * `source` defaults to `'profile'` because that's the original (and
   * still the dominant) caller — `nearby-places` seeding from
   * `user.location`. Pass an explicit source for other situations.
   */
  setTrustedLocation(
    location: GeoLocationType,
    accuracy: number = 50,
    source: LocationSource = 'profile'
  ): void {
    if (!location?.latitude || !location?.longitude) return;
    // Only seed the baseline when we don't already have an equally-or-
    // more precise fix. Otherwise this would actively destroy a real
    // device fix (e.g. an 11m WiFi fix from `updateUserLocation`) by
    // overwriting it with a much coarser "trusted" value (50m).
    //
    // Manual picks are an explicit user intent, so they bypass the
    // accuracy gate — if the user just tapped a spot on the map, they
    // mean it, even if a more precise GPS fix happens to exist.
    if (
      source !== 'manual' &&
      this.locationAccuracy !== null &&
      this.locationAccuracy <= accuracy
    ) {
      return;
    }
    this.applyLocation(location, accuracy, source, Date.now());
  }

  /**
   * Override the active location with a user-picked point (e.g. via the
   * map selector). Treated as authoritative — bypasses the accuracy
   * gate of `setTrustedLocation` because it represents an explicit user
   * intent, not a GPS reading we have to trust cautiously.
   */
  setManualLocation(location: GeoLocationType): void {
    if (!location?.latitude || !location?.longitude) return;
    // 30m roughly matches a "good" GPS fix; using a tight value here
    // also means a later coarse cell-tower fallback won't quietly
    // overwrite the user's manual pick.
    this.applyLocation(location, 30, 'manual', Date.now());
  }

  /**
   * Returns the currently known location synchronously (cached, fast fix,
   * or high-accuracy refinement — whichever is most recent). May be null
   * if we've never had a fix and no cache exists.
   */
  getCurrentLocation(): GeoLocationType | null {
    return this.location ?? null;
  }

  /**
   * Returns the full snapshot for the currently known location, or
   * `null` if we've never had one. Use this in UI that needs to show
   * source / staleness in addition to coordinates.
   */
  getLocationSnapshot(): LocationSnapshot | null {
    return this.locationSnapshot$.value;
  }

  /**
   * Single point of mutation for `this.location` and friends. Updates
   * all in-memory mirrors, optionally writes the cache, and emits both
   * `location$` and `locationSnapshot$` in lockstep. Centralizing this
   * makes it impossible to forget one of those steps from a caller.
   */
  private applyLocation(
    location: GeoLocationType,
    accuracy: number | null,
    source: LocationSource,
    timestamp: number,
    opts: { persist?: boolean } = {}
  ): void {
    const persist = opts.persist ?? true;
    this.location = location;
    this.locationAccuracy = accuracy;
    this.locationSource = source;
    this.locationTimestamp = timestamp;
    if (this.user) {
      this.user.location = location;
    }
    if (persist) {
      this.writeCachedLocation(location, accuracy, timestamp);
    }
    this.location$.next(location);
    // Carry over the previous displayName if the new fix is essentially
    // the same point — avoids a momentary "label-blank → label-back"
    // flicker while the (cached) reverse-geocode round-trip resolves.
    const previous = this.locationSnapshot$.value;
    const reuseDisplayName = previous?.displayName && this.isWithinReverseGeocodeRadius(previous.location, location)
      ? previous.displayName
      : undefined;
    this.locationSnapshot$.next({
      location,
      accuracy,
      source,
      timestamp,
      displayName: reuseDisplayName,
    });
    void this.maybeReverseGeocode(location);
  }

  private isWithinReverseGeocodeRadius(a: GeoLocationType, b: GeoLocationType): boolean {
    const lat1 = parseFloat(a.latitude);
    const lng1 = parseFloat(a.longitude);
    const lat2 = parseFloat(b.latitude);
    const lng2 = parseFloat(b.longitude);
    if (![lat1, lng1, lat2, lng2].every(Number.isFinite)) return false;
    // Equirectangular approximation — plenty for a 50m gate.
    const R = 6371e3;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const meanLat = ((lat1 + lat2) / 2) * Math.PI / 180;
    const dist = Math.sqrt((dLat * R) ** 2 + (dLng * R * Math.cos(meanLat)) ** 2);
    return dist < SharedDataService.REVERSE_GEOCODE_DISTANCE_M;
  }

  /**
   * Asks the server to reverse-geocode the current coordinate to a
   * short human-readable label. Skipped when the user hasn't moved
   * more than {@link REVERSE_GEOCODE_DISTANCE_M} since the last
   * successful lookup, and when one is already in flight. Failures
   * are swallowed — the UI degrades to "no label".
   *
   * The result is re-emitted on `locationSnapshot$` only; we keep
   * `location$` to plain coordinates for backwards-compatibility.
   */
  private async maybeReverseGeocode(location: GeoLocationType): Promise<void> {
    const lat = parseFloat(location?.latitude);
    const lng = parseFloat(location?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    if (this.reverseGeocodeInFlight) return;

    if (
      this.lastReverseGeocodeLat !== null &&
      this.lastReverseGeocodeLng !== null
    ) {
      const cached = new GeoLocationType();
      cached.latitude = this.lastReverseGeocodeLat.toString();
      cached.longitude = this.lastReverseGeocodeLng.toString();
      if (this.isWithinReverseGeocodeRadius(cached, location)) {
        return;
      }
    }

    this.reverseGeocodeInFlight = true;
    try {
      const result: GeolocationGetAddressFromCoordinatesResponse =
        await new GeolocationGetAddressFromCoordinatesRequest({
          latitude: lat.toString(),
          longitude: lng.toString(),
          language: 'de',
        }).execute();
      const displayName = result?.displayName?.trim();
      if (!displayName) return;

      this.lastReverseGeocodeLat = lat;
      this.lastReverseGeocodeLng = lng;
      const current = this.locationSnapshot$.value;
      // Only patch the snapshot if it still represents the coordinate
      // we just resolved — otherwise we'd overwrite a newer fix with
      // the label of an older one.
      if (
        current &&
        current.location?.latitude === location.latitude &&
        current.location?.longitude === location.longitude
      ) {
        this.locationSnapshot$.next({ ...current, displayName });
      }
    } catch (e) {
      console.log('[SharedDataService / location] Reverse-geocode failed:', e);
    } finally {
      this.reverseGeocodeInFlight = false;
    }
  }

  private readCachedLocation(): { location: GeoLocationType; accuracy: number | null; savedAt: number } | null {
    try {
      const raw = localStorage.getItem(SharedDataService.LOCATION_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.latitude || !parsed?.longitude) return null;
      const loc = new GeoLocationType();
      loc.latitude = parsed.latitude;
      loc.longitude = parsed.longitude;
      const accuracy = typeof parsed.accuracy === 'number' ? parsed.accuracy : null;
      const savedAt = typeof parsed.savedAt === 'number' ? parsed.savedAt : Date.now();
      return { location: loc, accuracy, savedAt };
    } catch {
      return null;
    }
  }

  private writeCachedLocation(location: GeoLocationType, accuracy: number | null, savedAt: number = Date.now()): void {
    try {
      localStorage.setItem(
        SharedDataService.LOCATION_CACHE_KEY,
        JSON.stringify({
          latitude: location.latitude,
          longitude: location.longitude,
          accuracy,
          savedAt,
        })
      );
    } catch {
      // localStorage may be unavailable (private mode, quota); ignore.
    }
  }

  hasCachedLocation(): boolean {
    return !!(this.location?.latitude && this.location?.longitude);
  }

  async getFacility(id: string) {
    // Free-text news/gatherings (admin fallback) carry no facility id; don't try to
    // resolve one — a lookup with an empty id would fetch a bogus facility (and could
    // reject the whole home feed when called inside Promise.all).
    if (!id) {
      return undefined;
    }
    if (!this.facilities.has(id)) {
      console.log(`[SharedDataService / facility] Haven't seen facility id ${id} before. Fetching now...`);
      const facility: Facility = await new FacilityGetRequest({id: id}).execute();
      this.facilities.set(id, facility);
    }
    console.log(`[SharedDataService / facility] Returning facility ${id} from store.`);
    return this.facilities.get(id);
  }

  async queryNewsPage() {
    const newsResponse: NewsQueryPaginatedResponse = await new NewsQueryPaginatedRequest({
      page: this.newsPage,
      pageSize: this.newsPageSize,
      filter: {
        datePublished: new Date()
      }
    }).execute();
    if (newsResponse.newsList) {
      // Use Promise.all to ensure all news items are processed before continuing
      await Promise.all(newsResponse.newsList.map(async (news: News) => {
        news.owningFacilityObj = await this.getFacility(news.owningFacility);
        await this.setNews(news.id, news);
        return news;
      }));
    }
    this.newsPage++;
    this.newsTotalCount = newsResponse.totalCount;
  }


  async getNews(id: string) {
    if (!this.news.has(id)) {
      console.log(`[SharedDataService / news] Haven't seen news id ${id} before. Fetching now...`);
      const newsItem: News = await new NewsGetRequest({id: id}).execute();
      this.news.set(id, newsItem);
    }
    console.log(`[SharedDataService / news] Returning news id ${id} from store.`);
    return this.news.get(id);
  }

  async setNews(id: string, newsItem: News) {
    console.log(`[SharedDataService / news] Saved news id ${id} to store.`);
    this.news.set(id, newsItem);
  }

  async getNextNewsItem(currentId: string): Promise<News> {
    const newsKeys = Array.from(this.news.keys());
    const currentIndex = newsKeys.indexOf(currentId);

    if (currentIndex !== -1 && currentIndex < newsKeys.length - 1) {
      return this.news.get(newsKeys[currentIndex + 1]);
    } else {
      await this.queryNewsPage();
      const updatedNewsKeys = Array.from(this.news.keys());
      if (currentIndex < updatedNewsKeys.length - 1) {
        return this.news.get(updatedNewsKeys[currentIndex + 1]);
      } else {
        return undefined;
      }
    }
  }

  async getPreviousNewsItem(currentId: string): Promise<News> {
    const newsKeys = Array.from(this.news.keys());
    const currentIndex = newsKeys.indexOf(currentId);

    if (currentIndex > 0) {
      return this.news.get(newsKeys[currentIndex - 1]);
    } else {
      await this.queryNewsPage();
      const updatedNewsKeys = Array.from(this.news.keys());
      if (currentIndex > 0) {
        return this.news.get(updatedNewsKeys[currentIndex - 1]);
      } else {
        return undefined;
      }
    }
  }

  async queryLifeHacks() {
    let lifeHacks: LifeHack[];
    if (this.lifeHacks.size === 0) {
      lifeHacks = await new LifeHackQueryRequest({ isActive: true }).execute();
      lifeHacks.map(async (lifeHack: LifeHack) => {
        this.lifeHacks.set(lifeHack.id, lifeHack);
      });
    }
    return this.lifeHacks;
  }

  async getNewsByOwningFacility(facilityId: string) {
    if (!this.newsByFacility.has(facilityId)) {
      console.log(`[SharedDataService / newsByFacility] Haven't seen news for facility id ${facilityId} before. Fetching now...`);
      const newsByFacility: News[] = await new NewsQueryRequest({filter: {owningFacility: facilityId}}).execute();
      this.newsByFacility.set(facilityId, newsByFacility);

      // store each of the news in the news map too
      for (let newsItem of newsByFacility) {
        this.news.set(newsItem.id, newsItem);
      }
    }
    console.log(`[SharedDataService / newsByFacility] Returning news for facility id ${facilityId} from store.`);
    return this.newsByFacility.get(facilityId);
  }

  async getNewsPageByOwningFacility(facilityId: string, page: number, pageSize: number): Promise<NewsQueryPaginatedResponse> {
    const response: NewsQueryPaginatedResponse = await new NewsQueryPaginatedRequest({
      page,
      pageSize,
      // datePublished is turned into $lte by the executor — hide scheduled/unpublished
      // news from end users, same as the home feed (queryNewsPage)
      filter: {owningFacility: facilityId, datePublished: new Date()},
      // keep the list monotonic w.r.t. the publish date so the facility news page
      // can split it into "aktuell" / "Ältere News" across paginated loads
      sortByPublishedDate: true
    }).execute();

    // Keep the shared news map in sync so the news detail page's
    // next/previous navigation can resolve these items too.
    if (response?.newsList) {
      for (const newsItem of response.newsList) {
        this.news.set(newsItem.id, newsItem);
      }
    }
    return response;
  }

  async getGatheringsByOwningFacility(facilityId: string) {
    if (!this.gatheringsByFacility.has(facilityId)) {
      console.log(`[SharedDataService / gatheringsByFacility] Haven't seen gatherings for facility id ${facilityId} before. Fetching now...`);
      const gatherings: Gathering[] = await new GatheringQueryRequest({filter: {owningFacility: facilityId}}).execute();
      this.gatheringsByFacility.set(facilityId, gatherings);
    }
    console.log(`[SharedDataService / gatheringsByFacility] Returning gatherings for facility id ${facilityId} from store.`);
    return this.gatheringsByFacility.get(facilityId);
  }

  async getPicturesByOwningFacility(facilityId: string) {
    if (!this.picturesByFacility.has(facilityId)) {
      console.log(`[SharedDataService / picturesByFacility] Haven't seen pictures for facility id ${facilityId} before. Fetching now...`);
      const pictures: Picture[] = await new FacilityPictureQueryRequest({owningFacility: facilityId}).execute();
      this.picturesByFacility.set(facilityId, pictures);
    }
    console.log(`[SharedDataService / picturesByFacility] Returning pictures for facility id ${facilityId} from store.`);
    return this.picturesByFacility.get(facilityId);
  }

  updatePicturesForFacility(facilityId: string, pictures: Picture[]) {
    console.log(`[SharedDataService / picturesByFacility] Updating pictures for facility id ${facilityId} in store.`);
    this.picturesByFacility.set(facilityId, pictures);
  }

  async getVideosByOwningFacility(facilityId: string) {
    if (!this.videosByFacility.has(facilityId)) {
      console.log(`[SharedDataService / videosByFacility] Haven't seen videos for facility id ${facilityId} before. Fetching now...`);
      const facility: Facility = await this.getFacility(facilityId);
      if (facility?.videos?.length) {
        const videos: VideoFile[] = await new VideoQueryRequest({idList: facility.videos}).execute();
        this.videosByFacility.set(facilityId, videos);
      }
    }
    console.log(`[SharedDataService / videosByFacility] Returning videos for facility id ${facilityId} from store.`);
    return this.videosByFacility.get(facilityId);
  }

  async refreshVideosByOwningFacility(facilityId: string) {
    console.log(`[SharedDataService / videosByFacility] Refreshing videos for facility id ${facilityId} in store.`);
    // Remove the cached videos to force a fresh fetch next time
    this.videosByFacility.delete(facilityId);
    // Return the newly fetched videos
    return await this.getVideosByOwningFacility(facilityId);
  }

  async queryGatheringPage() {
    // Pass through the central location so the server can re-rank
    // results by proximity ("Events in deiner Nähe" — the heading we
    // already show on the home page is now actually true).
    const userLocation = this.location ?? undefined;
    const gatheringResponse: GatheringQueryPaginatedResponse = await new GatheringQueryPaginatedRequest({
      page: this.gatheringPage,
      pageSize: this.gatheringPageSize,
      filter: { startingAt: { $gt: dayjs() } },
      sorting: { startingAt: 1 },
      userLocation
    }).execute();
    if (gatheringResponse.gatherings) {
      // Use Promise.all to ensure all gatherings are processed before continuing
      await Promise.all(gatheringResponse.gatherings.map(async (gathering: Gathering) => {
        this.gatherings.set(gathering.id, gathering);
        return gathering;
      }));
    }
    this.gatheringPage++;
    this.gatheringTotalCount = gatheringResponse.totalCount;
  }

  async getGathering(id: string) {
    if (!this.gatherings.has(id)) {
      console.log(`[SharedDataService / gathering] Haven't seen gathering id ${id} before. Fetching now...`);
      const gathering: Gathering = await new GatheringGetRequest({id: id}).execute();
      this.gatherings.set(id, gathering);
    }
    console.log(`[SharedDataService / gathering] Returning gathering id ${id} from store.`);
    return this.gatherings.get(id);
  }

  async getNextGatheringItem(currentId: string): Promise<Gathering> {
    const gatheringKeys = Array.from(this.gatherings.keys());
    const currentIndex = gatheringKeys.indexOf(currentId);

    if (currentIndex !== -1 && currentIndex < gatheringKeys.length - 1) {
      return this.gatherings.get(gatheringKeys[currentIndex + 1]);
    } else {
      await this.queryGatheringPage();
      const updatedGatheringKeys = Array.from(this.gatherings.keys());
      if (currentIndex < updatedGatheringKeys.length - 1) {
        return this.gatherings.get(updatedGatheringKeys[currentIndex + 1]);
      } else {
        return undefined;
      }
    }
  }

  async getPreviousGatheringItem(currentId: string): Promise<Gathering> {
    const gatheringKeys = Array.from(this.gatherings.keys());
    const currentIndex = gatheringKeys.indexOf(currentId);

    if (currentIndex > 0) {
      return this.gatherings.get(gatheringKeys[currentIndex - 1]);
    } else {
      await this.queryGatheringPage();
      const updatedGatheringKeys = Array.from(this.gatherings.keys());
      if (currentIndex > 0) {
        return this.gatherings.get(updatedGatheringKeys[currentIndex - 1]);
      } else {
        return undefined;
      }
    }
  }

  async reloadUser(): Promise<User> {
    this.user = await new UserGetRequest().execute();
    return this.user;
  }

  async getUser(): Promise<User> {
    console.log('getUser');
    if (!this.user) {
      console.log(`[SharedDataService / user] Haven't seen user before. Fetching now...`);
      this.user = await new UserGetRequest().execute();
      this.publishSubscribedFacilities();
    }
    console.log(`[SharedDataService / user] Returning user from store.`);
    return this.user;
  }

  async updateUser(user: User): Promise<User> {
    console.log('updateUser');
    console.log(`[SharedDataService / user] Updating user.`);

    await new UserUpdateRequest(user).execute();
    this.user = user;
    this.webAuthenticationService.setUser(user);
    this.publishSubscribedFacilities();
    return this.user;
  }

  /**
   * Re-emit the current user's `likedFacilities` on
   * `subscribedFacilities$`. Always emits a fresh `Set` instance so
   * downstream change detection / identity comparisons trigger.
   */
  private publishSubscribedFacilities(): void {
    const ids = Array.isArray(this.user?.likedFacilities)
      ? this.user.likedFacilities
      : [];
    this.subscribedFacilities$.next(new Set(ids));
  }

  async updateUserFcmToken(fcmToken: string): Promise<void> {
    console.log(`[SharedDataService / user] Updating FCM token: ${fcmToken.substring(0, 20)}...`);
    // get the latest user data before updating
    this.user = await new UserGetRequest().execute();
    // set token and update
    this.user.fcmRegistrationToken = fcmToken;
    await new UserUpdateRequest(this.user).execute();
  }

  async unsetUser(): Promise<void> {
    this.user = undefined;
    this.publishSubscribedFacilities();
  }

  /**
   * Returns the authoritative current location for the entire app.
   *
   * This is the single source of truth that every list / distance
   * calculation should use. The previous implementation re-fetched the
   * user record and preferred `user.location`, which caused the home
   * page to silently disagree with the map page (one preferring the
   * server-saved profile location, the other the freshest device fix).
   *
   * Resolution order:
   *   1. Whatever the central store currently has (live device fix,
   *      cache, manual pick, or already-seeded profile baseline).
   *   2. The signed-in user's `user.location`, used as a *baseline*
   *      seed (`setTrustedLocation` with `'profile'`).
   *   3. A fresh device fix via `updateUserLocation()`.
   *
   * Returns `null` only when none of the above succeeded (denied
   * permission + signed-out + no cache); callers should handle that.
   */
  async getUserLocation(): Promise<GeoLocationType | null> {
    if (this.location) {
      return this.location;
    }
    if (this.user?.location?.latitude && this.user?.location?.longitude) {
      this.setTrustedLocation(this.user.location, 50, 'profile');
      if (this.location) {
        return this.location;
      }
    }
    await this.updateUserLocation();
    return this.location ?? null;
  }

  /**
   * Two-phase location refresh.
   *
   *  - Phase 1 (default, `highAccuracy: false`): network/Wi-Fi/cell
   *    positioning. Typically resolves in <1s with ~50-100m precision —
   *    plenty for "what's nearby" listings. Use this on the home screen
   *    and any time you just need a rough position fast.
   *
   *  - Phase 2 (`highAccuracy: true`): GPS-quality fix. May take 5-15s on
   *    a cold start outside Wi-Fi. Use this for the map page where the
   *    user marker should be as accurate as possible. Fire-and-forget
   *    it after the fast phase has already painted the map, then react
   *    to `location$` to move the marker once it lands.
   *
   * Both phases write to `this.location`, push to `location$`, and persist
   * to localStorage on success. Concurrent calls for the same phase are
   * deduplicated.
   */
  async updateUserLocation(opts: { highAccuracy?: boolean } = {}): Promise<void> {
    const highAccuracy = !!opts.highAccuracy;
    const slot = highAccuracy ? 'highAccuracyLocationFetchPromise' : 'fastLocationFetchPromise';

    if (this[slot]) {
      return this[slot];
    }

    // IMPORTANT: high-accuracy uses `maximumAge: 0`. The W3C geolocation
    // spec lets the platform satisfy `getCurrentPosition` from its cached
    // fix as long as the cached fix is within `maximumAge` — regardless of
    // the cached fix's accuracy. Without `maximumAge: 0` the OS happily
    // returns the *low-accuracy* result phase 1 just produced, so phase 2
    // appears to never refine the position.
    const options = highAccuracy
      ? { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      : { enableHighAccuracy: false, timeout: 5000, maximumAge: 5 * 60 * 1000 };

    this[slot] = (async () => {
      try {
        console.log(`Trying to fetch location (highAccuracy=${highAccuracy})`);
        const resp = await this.geolocation.getCurrentPosition(options);
        const fresh = new GeoLocationType();
        fresh.latitude = resp.coords.latitude.toString();
        fresh.longitude = resp.coords.longitude.toString();
        const newAccuracy = (resp.coords as any)?.accuracy ?? null;
        console.log(`Got location (highAccuracy=${highAccuracy})`, fresh, 'accuracy:', newAccuracy);

        // Reject fixes that are significantly worse than what we already
        // trust. The OS sometimes returns a coarse cell-tower fallback
        // (e.g. 1600m accuracy) — accepting that would yank the pin
        // 1+ km away from the user's real position.
        //
        // Only a fresh same-session GPS fix is a real precision baseline.
        // Everything else either represents user intent or unknown staleness
        // and would cause us to throw away perfectly good new fixes:
        //   - 'manual': explicit user pick on the map; a 30m baseline would
        //     silently swallow a ~50-100m phase-1 WiFi fix.
        //   - 'gps-cache': a previous fix from disk, possibly hours or days
        //     old. Its tight accuracy says nothing about whether the
        //     coordinates are still valid (user may have travelled). Gating
        //     against it means an explicit "use current location" tap can
        //     never replace a stale ~15m cached fix with a fresh ~40m WiFi
        //     fix — exactly the bug we hit in practice.
        //   - 'profile': server-side stored location, similar staleness
        //     concerns as the cache.
        const gateAgainstAccuracy = this.locationSource === 'gps';
        const accept =
          !gateAgainstAccuracy ||
          this.locationAccuracy === null ||
          newAccuracy === null ||
          newAccuracy <= this.locationAccuracy * SharedDataService.ACCURACY_DEGRADATION_FACTOR;

        if (!accept) {
          return;
        }

        this.applyLocation(fresh, newAccuracy, 'gps', Date.now());
      } catch (error) {
        console.log(`Error getting location (highAccuracy=${highAccuracy})`, error);
        // Keep any previously cached location so downstream queries can
        // still proceed with a reasonable guess.
        if (this.user && this.location) {
          this.user.location = this.location;
        }
      }
    })();

    try {
      await this[slot];
    } finally {
      this[slot] = null;
    }
  }

  async queryFacilityClubsPage() {
    let userLocation = await this.getUserLocation();
    const clubResponse: FacilityQueryPaginatedResponse = await new FacilityQueryPaginatedRequest({
      page: this.facilityClubsPage,
      pageSize: this.facilityClubsPageSize,
      filter: { isClub: true },
      userLocation: userLocation
    }).execute();
    if (clubResponse.facilities) {
      // Use Promise.all to ensure all facility clubs are processed before continuing
      await Promise.all(clubResponse.facilities.map(async (facility: Facility) => {
        this.facilityClubs.set(facility.id, facility);
        return facility;
      }));
    }
    this.facilityClubsPage++;
    this.facilityClubsTotalCount = clubResponse.totalCount;
  }

  async getNextFacilityClubsItem(currentId: string): Promise<Facility> {
    const facilityKeys = Array.from(this.facilityClubs.keys());
    const currentIndex = facilityKeys.indexOf(currentId);

    if (currentIndex !== -1 && currentIndex < facilityKeys.length - 1) {
      return this.facilityClubs.get(facilityKeys[currentIndex + 1]);
    }
    await this.queryFacilityClubsPage();
    const updatedFacilityKeys = Array.from(this.facilityClubs.keys());
    const updatedIndex = updatedFacilityKeys.indexOf(currentId);
    if (updatedIndex !== -1 && updatedIndex < updatedFacilityKeys.length - 1) {
      return this.facilityClubs.get(updatedFacilityKeys[updatedIndex + 1]);
    }
    return undefined;
  }

  async getPreviousFacilityClubsItem(currentId: string): Promise<Facility> {
    const facilityKeys = Array.from(this.facilityClubs.keys());
    const currentIndex = facilityKeys.indexOf(currentId);

    if (currentIndex > 0) {
      return this.facilityClubs.get(facilityKeys[currentIndex - 1]);
    }
    await this.queryFacilityClubsPage();
    const updatedFacilityKeys = Array.from(this.facilityClubs.keys());
    const updatedIndex = updatedFacilityKeys.indexOf(currentId);
    if (updatedIndex > 0) {
      return this.facilityClubs.get(updatedFacilityKeys[updatedIndex - 1]);
    }
    return undefined;
  }

  async queryFacilityConsultingPage() {
    const facilityResponse: FacilityQueryPaginatedResponse = await new FacilityQueryPaginatedRequest({
      page: this.facilityConsultingPage,
      pageSize: this.facilityConsultingPageSize,
      filter: { isConsulting: true },
      userLocation: await this.getUserLocation()
    }).execute();
    if (facilityResponse.facilities) {
      // Use Promise.all to ensure all consulting facilities are processed before continuing
      await Promise.all(facilityResponse.facilities.map(async (facility: Facility) => {
        this.facilityConsulting.set(facility.id, facility);
        return facility;
      }));
    }
    this.facilityConsultingPage++;
    this.facilityConsultingTotalCount = facilityResponse.totalCount;
  }

  async getNextConsultingFacilityItem(currentId: string): Promise<Facility> {
    const facilityKeys = Array.from(this.facilityConsulting.keys());
    const currentIndex = facilityKeys.indexOf(currentId);

    if (currentIndex !== -1 && currentIndex < facilityKeys.length - 1) {
      return this.facilityConsulting.get(facilityKeys[currentIndex + 1]);
    }
    await this.queryFacilityConsultingPage();
    const updatedFacilityKeys = Array.from(this.facilityConsulting.keys());
    const updatedIndex = updatedFacilityKeys.indexOf(currentId);
    if (updatedIndex !== -1 && updatedIndex < updatedFacilityKeys.length - 1) {
      return this.facilityConsulting.get(updatedFacilityKeys[updatedIndex + 1]);
    }
    return undefined;
  }

  async getPreviousConsultingFacilityItem(currentId: string): Promise<Facility> {
    const facilityKeys = Array.from(this.facilityConsulting.keys());
    const currentIndex = facilityKeys.indexOf(currentId);

    if (currentIndex > 0) {
      return this.facilityConsulting.get(facilityKeys[currentIndex - 1]);
    }
    await this.queryFacilityConsultingPage();
    const updatedFacilityKeys = Array.from(this.facilityConsulting.keys());
    const updatedIndex = updatedFacilityKeys.indexOf(currentId);
    if (updatedIndex > 0) {
      return this.facilityConsulting.get(updatedFacilityKeys[updatedIndex - 1]);
    }
    return undefined;
  }

  async queryFacilityOtherPage() {
    const facilityResponse: FacilityQueryPaginatedResponse = await new FacilityQueryPaginatedRequest({
      page: this.facilityOtherPage,
      pageSize: this.facilityOtherPageSize,
      // "Weitere Angebote" collects everything that is neither a club nor a
      // consulting facility – this includes facilities flagged `isOther` as
      // well as ones that have none of the main categories selected.
      filter: { isClub: { $ne: true }, isConsulting: { $ne: true } },
      userLocation: await this.getUserLocation()
    }).execute();
    if (facilityResponse.facilities) {
      await Promise.all(facilityResponse.facilities.map(async (facility: Facility) => {
        this.facilityOther.set(facility.id, facility);
        return facility;
      }));
    }
    this.facilityOtherPage++;
    this.facilityOtherTotalCount = facilityResponse.totalCount;
  }

  async getNextOtherFacilityItem(currentId: string): Promise<Facility> {
    const facilityKeys = Array.from(this.facilityOther.keys());
    const currentIndex = facilityKeys.indexOf(currentId);

    if (currentIndex !== -1 && currentIndex < facilityKeys.length - 1) {
      return this.facilityOther.get(facilityKeys[currentIndex + 1]);
    }
    await this.queryFacilityOtherPage();
    const updatedFacilityKeys = Array.from(this.facilityOther.keys());
    const updatedIndex = updatedFacilityKeys.indexOf(currentId);
    if (updatedIndex !== -1 && updatedIndex < updatedFacilityKeys.length - 1) {
      return this.facilityOther.get(updatedFacilityKeys[updatedIndex + 1]);
    }
    return undefined;
  }

  async getPreviousOtherFacilityItem(currentId: string): Promise<Facility> {
    const facilityKeys = Array.from(this.facilityOther.keys());
    const currentIndex = facilityKeys.indexOf(currentId);

    if (currentIndex > 0) {
      return this.facilityOther.get(facilityKeys[currentIndex - 1]);
    }
    await this.queryFacilityOtherPage();
    const updatedFacilityKeys = Array.from(this.facilityOther.keys());
    const updatedIndex = updatedFacilityKeys.indexOf(currentId);
    if (updatedIndex > 0) {
      return this.facilityOther.get(updatedFacilityKeys[updatedIndex - 1]);
    }
    return undefined;
  }

  /**
   * Loads the next page of "next" offers for one category, distance-sorted
   * around the central location (the server's $geoNear does the sorting and
   * fills `distanceFromUser` in meters). Analog of queryFacilityClubsPage().
   */
  async queryNextOffersPage(offerType: NextOfferType): Promise<void> {
    const userLocation = await this.getUserLocation();
    const response: NextQueryPaginatedResponse = await new NextQueryPaginatedRequest({
      page: this.nextOffersPage[offerType],
      pageSize: this.nextOffersPageSize,
      filter: { offerType },
      userLocation: userLocation ?? undefined
    }).execute();
    for (const offer of response.offers ?? []) {
      this.nextOffers[offerType].set(offer.id, offer);
    }
    this.nextOffersPage[offerType]++;
    this.nextOffersTotalCount[offerType] = response.totalCount;
  }

  /**
   * Module status of the "next" categories (last successful sync, staleness,
   * emergency off-switch — NEXT_MODULE_PLAN.md §4.5/§4.6). Cached for the app
   * session; the home page decides visibility per category from this.
   */
  async getNextModuleStatus(forceRefresh: boolean = false): Promise<NextGetModuleStatusResponse> {
    if (!this.nextModuleStatus || forceRefresh) {
      this.nextModuleStatus = await new NextGetModuleStatusRequest({}).execute();
    }
    return this.nextModuleStatus;
  }

  /**
   * Drop all cached "next" offers and reset pagination — the sliders reload
   * distance-sorted for the (new) location on the next query.
   */
  resetNextOfferData(): void {
    for (const offerType of Object.values(NextOfferType)) {
      this.nextOffers[offerType] = new Map<string, NextOffer>();
      this.nextOffersPage[offerType] = 1;
      this.nextOffersTotalCount[offerType] = 999999;
    }
  }

  /**
   * Drop the cached news feed and reset pagination so the next
   * `queryNewsPage()` re-fetches the newest articles from page 1. Lets the
   * home feed pick up freshly published news (e.g. one a push just
   * announced) on view-enter / app-resume without the heavy full-page
   * `window.location.reload()` that pull-to-refresh uses.
   */
  async resetNewsData() {
    this.news = new Map<string, News>();
    this.newsPage = 1;
    this.newsTotalCount = 999999;
    await this.queryNewsPage();
  }

  async resetLocationRelatedData() {
    this.facilityClubs = new Map<string, Facility>();
    this.facilityClubsPage = 1;
    await this.queryFacilityClubsPage();

    this.facilityConsulting = new Map<string, Facility>();
    this.facilityConsultingPage = 1
    await this.queryFacilityConsultingPage();

    this.facilityOther = new Map<string, Facility>();
    this.facilityOtherPage = 1;
    await this.queryFacilityOtherPage();

    this.gatherings = new Map<string, Gathering>();
    this.gatheringPage = 1;
    await this.queryGatheringPage();

    // without this the "next" sliders would keep the old distance order
    // after a location change (NEXT_MODULE_PLAN.md §6.1)
    this.resetNextOfferData();
    for (const offerType of Object.values(NextOfferType)) {
      await this.queryNextOffersPage(offerType);
    }
  }

  updateFacility(facility: Facility) {
    console.log(`[SharedDataService / facility] Updating facility ${facility.id} in store.`);
    this.facilities.set(facility.id, facility);
  }

  isMapFilterCategoryEnabled(categoryName: string): boolean {
    return !this.mapFilterDisabledCategories.has(categoryName);
  }

  setMapFilterCategoryEnabled(categoryName: string, enabled: boolean): void {
    if (enabled) {
      this.mapFilterDisabledCategories.delete(categoryName);
    } else {
      this.mapFilterDisabledCategories.add(categoryName);
    }
  }
}
